import { useEffect, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, Mail, Trash2, LogOut, RefreshCw, Users, Eye, TrendingUp, Bell, MousePointerClick, Target, Images, Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { api, formatApiError } from "../api";
import { useAuth } from "../auth/AuthContext";
import { SEO } from "../components/SEO";
import { SITE } from "../data/siteData";

const STATUS_STYLES = {
  new: "bg-brand/15 text-brand border-brand/40",
  contacted: "bg-amber-500/15 text-amber-400 border-amber-500/40",
  booked: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40",
  closed: "bg-white/10 text-white/70 border-white/20",
};

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-[#121212] border border-white/10 rounded-xl p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs font-heading font-bold tracking-widest uppercase text-white/60">{label}</span>
      <Icon size={17} className="text-brand" />
    </div>
    <p className="font-heading text-3xl font-extrabold text-white">{value}</p>
  </div>
);

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [posts, setPosts] = useState([]);
  const [bTopic, setBTopic] = useState("");
  const [bKeywords, setBKeywords] = useState("");
  const [bCity, setBCity] = useState("");
  const [generating, setGenerating] = useState(false);
  const [gType, setGType] = useState("photo");
  const [gUrl, setGUrl] = useState("");
  const [gCaption, setGCaption] = useState("");
  const [gAdding, setGAdding] = useState(false);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, l, g, b] = await Promise.all([
        api.get("/analytics/stats"),
        api.get(filter ? `/leads?status=${filter}` : "/leads"),
        api.get("/gallery"),
        api.get("/blog/all"),
      ]);
      setStats(s.data);
      setLeads(l.data.leads);
      setGallery(g.data.items);
      setPosts(b.data.posts);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user === null) navigate("/admin", { replace: true });
    else if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filter]);

  const setStatus = async (lead_id, status) => {
    try {
      await api.patch(`/leads/${lead_id}`, { status });
      setLeads((ls) => ls.map((l) => (l.lead_id === lead_id ? { ...l, status } : l)));
      toast.success("Status updated");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (lead_id) => {
    try {
      await api.delete(`/leads/${lead_id}`);
      setLeads((ls) => ls.filter((l) => l.lead_id !== lead_id));
      toast.success("Lead deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addGalleryItem = async (e) => {
    e.preventDefault();
    if (!gUrl.trim() || gAdding) return;
    setGAdding(true);
    try {
      const { data } = await api.post("/gallery", { type: gType, url: gUrl.trim(), caption: gCaption.trim() });
      setGallery((g) => [data, ...g]);
      setGUrl("");
      setGCaption("");
      toast.success("Added to gallery");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setGAdding(false);
    }
  };

  const removeGalleryItem = async (item_id) => {
    try {
      await api.delete(`/gallery/${item_id}`);
      setGallery((g) => g.filter((i) => i.item_id !== item_id));
      toast.success("Removed from gallery");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const generatePost = async (e) => {
    e.preventDefault();
    if (!bTopic.trim() || generating) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/blog/generate", { topic: bTopic.trim(), keywords: bKeywords.trim(), city: bCity.trim() }, { timeout: 180000 });
      setPosts((p) => [data, ...p]);
      setBTopic("");
      setBKeywords("");
      setBCity("");
      toast.success("Blog post drafted — review and publish below");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setGenerating(false);
    }
  };

  const togglePublish = async (post) => {
    const next = post.status === "published" ? "draft" : "published";
    try {
      await api.patch(`/blog/${post.post_id}`, { status: next });
      setPosts((ps) => ps.map((p) => (p.post_id === post.post_id ? { ...p, status: next } : p)));
      toast.success(next === "published" ? "Post is live on /blog" : "Post unpublished");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deletePost = async (post_id) => {
    try {
      await api.delete(`/blog/${post_id}`);
      setPosts((ps) => ps.filter((p) => p.post_id !== post_id));
      toast.success("Post deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-obsidian" data-testid="admin-dashboard">
      <SEO title="Dashboard | Chinook K9" description="Chinook K9 owner dashboard." path="/admin/dashboard" />
      <header className="border-b border-white/10 bg-black/60 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={SITE.logo} alt="Chinook K9" className="h-9 w-auto" />
            <span className="font-heading font-bold text-white text-sm tracking-wide">Owner Dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} data-testid="dashboard-refresh-btn" className="text-white/60 hover:text-brand transition-colors p-2" aria-label="Refresh">
              <RefreshCw size={17} />
            </button>
            <button
              onClick={async () => { await logout(); navigate("/admin"); }}
              data-testid="dashboard-logout-btn"
              className="flex items-center gap-2 text-sm text-white/60 hover:text-brand transition-colors"
            >
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-8 space-y-8">
        <div className="bg-[#121212] border border-white/10 rounded-xl overflow-hidden" data-testid="dashboard-leads-table">
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
            <h3 className="font-heading font-bold text-white text-sm tracking-wide">Lead Enquiries ({leads.length})</h3>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="leads-filter-select"
              className="bg-[#161616] border border-white/15 rounded-md px-3 py-1.5 text-xs text-white focus:outline-none focus:ring-1 focus:ring-brand"
            >
              <option value="">All statuses</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="booked">Booked</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-white/60 border-b border-white/10">
                  <th className="px-6 py-3 font-semibold">Date</th>
                  <th className="px-6 py-3 font-semibold">Client</th>
                  <th className="px-6 py-3 font-semibold">Dog</th>
                  <th className="px-6 py-3 font-semibold">Programs</th>
                  <th className="px-6 py-3 font-semibold">Contact Back</th>
                  <th className="px-6 py-3 font-semibold">Status</th>
                  <th className="px-6 py-3 font-semibold"></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7} className="px-6 py-10 text-center text-white/60">Loading leads...</td></tr>
                )}
                {!loading && leads.length === 0 && (
                  <tr><td colSpan={7} className="px-6 py-10 text-center text-white/60" data-testid="leads-empty">No leads yet — they'll appear here as the contact form is submitted.</td></tr>
                )}
                {leads.map((l) => (
                  <Fragment key={l.lead_id}>
                  <tr className="border-b border-white/5 hover:bg-white/[0.03] transition-colors cursor-pointer" data-testid={`lead-row-${l.lead_id.slice(0, 8)}`}
                    onClick={() => setExpanded(expanded === l.lead_id ? null : l.lead_id)}>
                    <td className="px-6 py-4 text-white/70 whitespace-nowrap">
                      <span className="flex items-center gap-2">
                        <ChevronDown size={14} className={`text-brand transition-transform ${expanded === l.lead_id ? "rotate-180" : ""}`} />
                        {new Date(l.created_at).toLocaleDateString("en-CA")}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-semibold text-white">{l.first_name} {l.last_name}</p>
                      <p className="text-xs text-white/60">{l.referral_source || "—"}</p>
                    </td>
                    <td className="px-6 py-4 text-white/70">{l.dog_name || "—"}<span className="text-white/60 text-xs block">{l.dog_breed}</span></td>
                    <td className="px-6 py-4 text-white/70 text-xs max-w-[160px]">{(l.programs || []).join(", ") || "—"}</td>
                    <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-2">
                        <a href={`tel:+1${(l.phone || "").replace(/\D/g, "")}`} data-testid={`lead-call-${l.lead_id.slice(0, 8)}`}
                          className="w-8 h-8 rounded-full bg-brand/15 text-brand flex items-center justify-center hover:bg-brand hover:text-white transition-colors" title={`Call ${l.phone}`}>
                          <Phone size={14} />
                        </a>
                        <a href={`mailto:${l.email}`} data-testid={`lead-email-${l.lead_id.slice(0, 8)}`}
                          className="w-8 h-8 rounded-full bg-white/10 text-white/70 flex items-center justify-center hover:bg-brand hover:text-white transition-colors" title={`Email ${l.email}`}>
                          <Mail size={14} />
                        </a>
                      </div>
                    </td>
                    <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={l.status}
                        onChange={(e) => setStatus(l.lead_id, e.target.value)}
                        data-testid={`lead-status-${l.lead_id.slice(0, 8)}`}
                        className={`border rounded-full px-3 py-1 text-xs font-semibold bg-transparent focus:outline-none cursor-pointer ${STATUS_STYLES[l.status] || STATUS_STYLES.new}`}
                      >
                        <option value="new" className="bg-[#161616]">New</option>
                        <option value="contacted" className="bg-[#161616]">Contacted</option>
                        <option value="booked" className="bg-[#161616]">Booked</option>
                        <option value="closed" className="bg-[#161616]">Closed</option>
                      </select>
                    </td>
                    <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => remove(l.lead_id)} data-testid={`lead-delete-${l.lead_id.slice(0, 8)}`}
                        className="text-white/30 hover:text-brand transition-colors" aria-label="Delete lead">
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                  {expanded === l.lead_id && (
                    <tr className="border-b border-white/5 bg-white/[0.02]" data-testid={`lead-details-${l.lead_id.slice(0, 8)}`}>
                      <td colSpan={7} className="px-6 py-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 text-sm">
                          {[
                            ["Email", l.email], ["Phone", l.phone],
                            ["Dog Birthdate", l.dog_birthdate], ["Behavioural Issues", l.behavioral_issues],
                            ["Training Goals", l.training_goals],
                            ["Bitten Before", l.bitten + (l.bitten_details ? ` — ${l.bitten_details}` : "")],
                            ["Medical Issues", l.medical_issues + (l.medical_details ? ` — ${l.medical_details}` : "")],
                            ["Additional Info", l.additional_info],
                            ["Reviewed Programs", l.reviewed_programs], ["Familiar With Tools", l.familiar_tools],
                            ["Veteran / First Responder", l.veteran], ["Submitted From", l.source_page],
                            ["Ad Click (gclid)", l.gclid || "—"], ["Campaign", [l.utm_source, l.utm_medium, l.utm_campaign].filter(Boolean).join(" / ") || "—"],
                          ].map(([k, v]) => (
                            <div key={k}>
                              <p className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-0.5">{k}</p>
                              <p className="text-white/85 whitespace-pre-wrap break-words">{v || "—"}</p>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {stats && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="dashboard-stats">
              <StatCard icon={Users} label="Total Leads" value={stats.total_leads} testid="stat-total-leads" />
              <StatCard icon={Bell} label="New Leads" value={stats.new_leads} testid="stat-new-leads" />
              <StatCard icon={Eye} label="Views (30d)" value={stats.views_30d} testid="stat-views-30d" />
              <StatCard icon={TrendingUp} label="Conversion (30d)" value={`${stats.conversion}%`} testid="stat-conversion" />
              <StatCard icon={MousePointerClick} label="Ads Clicks (30d)" value={stats.ads_30d} testid="stat-ads-clicks" />
              <StatCard icon={Target} label="Leads From Ads" value={stats.leads_ads} testid="stat-leads-from-ads" />
            </div>


            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 bg-[#121212] border border-white/10 rounded-xl p-6" data-testid="dashboard-chart">
                <h3 className="font-heading font-bold text-white mb-5 text-sm tracking-wide">Leads & Page Views — Last 14 Days</h3>
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={stats.daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="day" stroke="rgba(255,255,255,0.6)" fontSize={11} />
                    <YAxis stroke="rgba(255,255,255,0.6)" fontSize={11} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "#161616", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} />
                    <Area type="monotone" dataKey="views" stroke="#666" fill="rgba(255,255,255,0.06)" name="Page views" />
                    <Area type="monotone" dataKey="leads" stroke="#DC2626" fill="rgba(220,38,38,0.18)" name="Leads" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-4">
                <div className="bg-[#121212] border border-white/10 rounded-xl p-6" data-testid="dashboard-top-pages">
                  <h3 className="font-heading font-bold text-white mb-4 text-sm tracking-wide">Top Pages</h3>
                  <ul className="space-y-2.5">
                    {stats.top_pages.length === 0 && <li className="text-white/60 text-sm">No page views yet</li>}
                    {stats.top_pages.map((p) => (
                      <li key={p.page} className="flex justify-between text-sm">
                        <span className="text-white/70 truncate max-w-[180px]">{p.page}</span>
                        <span className="text-brand font-bold">{p.count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-[#121212] border border-white/10 rounded-xl p-6" data-testid="dashboard-by-program">
                  <h3 className="font-heading font-bold text-white mb-4 text-sm tracking-wide">Leads by Program</h3>
                  <ul className="space-y-2.5">
                    {stats.by_program.length === 0 && <li className="text-white/60 text-sm">No leads yet</li>}
                    {stats.by_program.map((p) => (
                      <li key={p.program} className="flex justify-between text-sm">
                        <span className="text-white/70">{p.program}</span>
                        <span className="text-brand font-bold">{p.count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-[#121212] border border-white/10 rounded-xl p-6" data-testid="dashboard-by-source">
                  <h3 className="font-heading font-bold text-white mb-4 text-sm tracking-wide">Traffic Sources (30d)</h3>
                  <ul className="space-y-2.5">
                    {stats.by_source.length === 0 && <li className="text-white/60 text-sm">No traffic yet</li>}
                    {stats.by_source.map((s) => (
                      <li key={s.source} className="flex justify-between text-sm">
                        <span className="text-white/70 truncate max-w-[180px]">{s.source}</span>
                        <span className={`font-bold ${s.source === "Google Ads" ? "text-brand" : "text-white/60"}`}>{s.count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="dashboard-traffic-times">
              <div className="bg-[#121212] border border-white/10 rounded-xl p-6" data-testid="chart-by-hour">
                <h3 className="font-heading font-bold text-white mb-1 text-sm tracking-wide">Busiest Hours — Last 30 Days</h3>
                <p className="text-xs text-white/60 mb-5">What time of day your site gets the most visits</p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={stats.by_hour}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="hour" stroke="rgba(255,255,255,0.6)" fontSize={10} interval={2} />
                    <YAxis stroke="rgba(255,255,255,0.6)" fontSize={11} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "#161616", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="clicks" fill="#DC2626" radius={[4, 4, 0, 0]} name="Visits" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-[#121212] border border-white/10 rounded-xl p-6" data-testid="chart-by-weekday">
                <h3 className="font-heading font-bold text-white mb-1 text-sm tracking-wide">Busiest Days — Last 30 Days</h3>
                <p className="text-xs text-white/60 mb-5">Which days of the week bring the most traffic</p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={stats.by_weekday}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="day" stroke="rgba(255,255,255,0.6)" fontSize={11} />
                    <YAxis stroke="rgba(255,255,255,0.6)" fontSize={11} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "#161616", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="clicks" fill="#DC2626" radius={[4, 4, 0, 0]} name="Visits" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}

        <div className="bg-[#121212] border border-white/10 rounded-xl p-6" data-testid="dashboard-gallery-manager">
          <h3 className="font-heading font-bold text-white text-sm tracking-wide mb-1">Gallery Manager</h3>
          <p className="text-xs text-white/60 mb-5">Add photos, YouTube links or video URLs — they appear on the /gallery page instantly.</p>
          <form onSubmit={addGalleryItem} className="flex flex-col md:flex-row gap-3 mb-6" data-testid="gallery-add-form">
            <select value={gType} onChange={(e) => setGType(e.target.value)} data-testid="gallery-type-select"
              className="bg-[#161616] border border-white/15 rounded-md px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-brand">
              <option value="photo">Photo</option>
              <option value="youtube">YouTube</option>
              <option value="video">Video (MP4 link)</option>
            </select>
            <input required value={gUrl} onChange={(e) => setGUrl(e.target.value)} data-testid="gallery-url-input"
              placeholder={gType === "youtube" ? "https://youtube.com/watch?v=..." : "https://... (image or video URL)"}
              className="flex-1 bg-[#161616] border border-white/15 rounded-md px-4 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-brand" />
            <input value={gCaption} onChange={(e) => setGCaption(e.target.value)} data-testid="gallery-caption-input"
              placeholder="Caption (optional)"
              className="md:w-56 bg-[#161616] border border-white/15 rounded-md px-4 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-brand" />
            <button type="submit" disabled={gAdding} data-testid="gallery-add-btn"
              className="bg-brand text-white font-heading font-bold text-sm px-6 py-2.5 rounded-full hover:bg-red-700 transition-colors disabled:opacity-60">
              {gAdding ? "Adding..." : "Add"}
            </button>
          </form>
          {gallery.length === 0 ? (
            <p className="text-white/60 text-sm" data-testid="gallery-admin-empty">No items yet.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="gallery-admin-grid">
              {gallery.map((item) => (
                <div key={item.item_id} className="relative group rounded-lg overflow-hidden border border-white/10 bg-black aspect-square" data-testid={`gallery-admin-item-${item.item_id.slice(0, 8)}`}>
                  {item.type === "photo" ? (
                    <img src={item.url} alt={item.caption || "gallery item"} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-white/60 gap-2 p-3">
                      <Images size={22} className="text-brand" />
                      <span className="text-[11px] text-center break-all line-clamp-2">{item.type === "youtube" ? "YouTube video" : "Video"}</span>
                    </div>
                  )}
                  <button onClick={() => removeGalleryItem(item.item_id)} data-testid={`gallery-delete-${item.item_id.slice(0, 8)}`}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 text-white/70 hover:text-brand flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Delete item">
                    <Trash2 size={13} />
                  </button>
                  <span className="absolute bottom-2 left-2 text-[10px] uppercase tracking-wider bg-black/70 text-white/80 rounded px-2 py-0.5">{item.type}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-[#121212] border border-white/10 rounded-xl p-6" data-testid="dashboard-blog-creator">
          <h3 className="font-heading font-bold text-white text-sm tracking-wide mb-1">AI Blog Creator</h3>
          <p className="text-xs text-white/60 mb-5">Enter a topic — AI writes an SEO blog post (title, meta description, headings, sections) and generates a cover image. Publish to /blog when ready.</p>
          <form onSubmit={generatePost} className="flex flex-col md:flex-row gap-3 mb-6" data-testid="blog-generate-form">
            <input required value={bTopic} onChange={(e) => setBTopic(e.target.value)} data-testid="blog-topic-input"
              placeholder="Topic, e.g. How to stop leash pulling"
              className="flex-1 bg-[#161616] border border-white/15 rounded-md px-4 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-brand" />
            <input value={bKeywords} onChange={(e) => setBKeywords(e.target.value)} data-testid="blog-keywords-input"
              placeholder="Keywords (optional)"
              className="md:w-48 bg-[#161616] border border-white/15 rounded-md px-4 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-brand" />
            <input value={bCity} onChange={(e) => setBCity(e.target.value)} data-testid="blog-city-input"
              placeholder="City (optional)"
              className="md:w-36 bg-[#161616] border border-white/15 rounded-md px-4 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-brand" />
            <button type="submit" disabled={generating} data-testid="blog-generate-btn"
              className="bg-brand text-white font-heading font-bold text-sm px-6 py-2.5 rounded-full hover:bg-red-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2 min-w-[150px]">
              {generating && <Loader2 size={15} className="animate-spin" />}
              {generating ? "Writing..." : "Generate Post"}
            </button>
          </form>
          {posts.length === 0 ? (
            <p className="text-white/60 text-sm" data-testid="blog-admin-empty">No posts yet — generate your first one above.</p>
          ) : (
            <div className="space-y-3" data-testid="blog-admin-list">
              {posts.map((p) => (
                <div key={p.post_id} className="flex items-center gap-4 bg-[#161616] border border-white/10 rounded-xl p-4" data-testid={`blog-admin-item-${p.slug}`}>
                  {p.cover_image && <img src={p.cover_image} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0 hidden sm:block" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-white text-sm truncate">{p.title}</p>
                    <p className="text-xs text-white/50 truncate">{p.meta_description || p.excerpt}</p>
                    <p className="text-xs text-white/40 mt-1">{new Date(p.created_at).toLocaleDateString("en-CA")} · {(p.sections || []).length} sections</p>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full px-3 py-1 border shrink-0 ${p.status === "published" ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" : "text-white/50 border-white/20 bg-white/5"}`}>
                    {p.status}
                  </span>
                  {p.status === "published" && (
                    <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" data-testid={`blog-view-${p.slug}`}
                      className="text-xs text-white/60 hover:text-brand transition-colors shrink-0">View</a>
                  )}
                  <button onClick={() => togglePublish(p)} data-testid={`blog-publish-${p.slug}`}
                    className={`text-xs font-bold rounded-full px-4 py-1.5 transition-colors shrink-0 ${p.status === "published" ? "bg-white/10 text-white/70 hover:bg-white/20" : "bg-brand text-white hover:bg-red-700"}`}>
                    {p.status === "published" ? "Unpublish" : "Publish"}
                  </button>
                  <button onClick={() => deletePost(p.post_id)} data-testid={`blog-delete-${p.slug}`}
                    className="text-white/30 hover:text-brand transition-colors shrink-0" aria-label="Delete post">
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
